/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { alpha } from '@mui/material/styles';

import { getCommandIntelligenceTokens } from '../../theme/commandIntelligenceTokens';

/**
 * @param {import('@mui/material').Theme & { validateMode?: Function }} theme
 * @param {{ isHorizontalDock?: boolean }} [options]
 */
export const getCommandIntelligenceDrawerContentStyles = (theme, { isHorizontalDock = false } = {}) => {
  const tokens = getCommandIntelligenceTokens(theme);
  const chromePaddingX = isHorizontalDock ? 2 : 2.5;
  const chromePaddingY = isHorizontalDock ? 1 : 1.75;

  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      fontFamily: tokens.fontStack,
      color: tokens.textPrimary,
    },
    header: {
      flexShrink: 0,
      px: chromePaddingX,
      py: chromePaddingY,
      borderBottom: `1px solid ${tokens.borderSubtle}`,
      bgcolor: tokens.surfaceChrome,
      backdropFilter: tokens.blurChrome,
      WebkitBackdropFilter: tokens.blurChrome,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      ...(isHorizontalDock
        ? {
            minHeight: 44,
          }
        : {}),
    },
    headerTitleRow: {
      minWidth: 0,
      flex: 1,
    },
    headerIcon: {
      color: tokens.accentIntelligence,
    },
    headerTitle: {
      fontWeight: 600,
      lineHeight: 1.2,
      minWidth: 0,
      color: tokens.textPrimary,
      ...(isHorizontalDock
        ? {
            fontSize: '0.9rem',
          }
        : {}),
    },
    headerActions: {
      flexShrink: 0,
    },
    sessionMeter: meterColor => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1,
      py: 0.25,
      borderRadius: tokens.radiusChip,
      bgcolor: alpha(meterColor, 0.12),
      color: meterColor,
      fontSize: '0.7rem',
      fontWeight: 600,
      lineHeight: 1.2,
      cursor: 'help',
      outline: 'none',
      transition: 'background-color 120ms ease',
      '&:hover': {
        bgcolor: alpha(meterColor, 0.2),
      },
      '&:focus-visible': {
        boxShadow: `0 0 0 2px ${alpha(tokens.accentPrimary, 0.4)}`,
      },
    }),
    sessionMeterTooltip: {
      maxWidth: 320,
      fontSize: '0.72rem',
      lineHeight: 1.4,
      px: 1.25,
      py: 0.75,
      whiteSpace: 'normal',
    },
    profileBar: {
      flexShrink: 0,
      px: chromePaddingX,
      py: isHorizontalDock ? 0.5 : 0.75,
      bgcolor: tokens.surfaceChrome,
      borderBottom: `1px solid ${tokens.borderSubtle}`,
      display: 'flex',
      gap: 0.75,
    },
    profileButton: {
      textTransform: 'none',
      minWidth: 0,
      px: 1,
      py: 0.25,
      fontSize: '0.72rem',
      borderRadius: tokens.radiusChip,
      borderColor: tokens.borderSubtle,
      color: tokens.textSecondary,
    },
    profileButtonActive: {
      textTransform: 'none',
      minWidth: 0,
      px: 1,
      py: 0.25,
      fontSize: '0.72rem',
      borderRadius: tokens.radiusChip,
      background: tokens.gradientCta,
      color: '#ffffff',
      boxShadow: tokens.elevationCta,
      '&:hover': {
        background: tokens.gradientCta,
        boxShadow: tokens.elevationCtaHover,
      },
    },
    contextBar: {
      flexShrink: 0,
      px: chromePaddingX,
      pt: isHorizontalDock ? 0.5 : 1,
      pb: isHorizontalDock ? 0.25 : 0.5,
      bgcolor: tokens.surfaceMain,
      borderBottom: `1px solid ${tokens.borderSubtle}`,
    },
    messagesViewport: {
      flexGrow: 1,
      overflow: 'auto',
      px: chromePaddingX,
      py: isHorizontalDock ? 0.75 : 2,
      bgcolor: 'transparent',
      maxWidth: isHorizontalDock ? '100%' : tokens.contentMaxWidthPx,
      width: '100%',
      mx: 'auto',
      minHeight: 0,
    },
    messagesViewportWelcome: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: isHorizontalDock ? 'flex-start' : 'center',
    },
    alertsBar: {
      flexShrink: 0,
      px: chromePaddingX,
      pt: isHorizontalDock ? 0.5 : 1,
      pb: 0,
      bgcolor: tokens.surfaceChrome,
    },
    alertCompact: {
      py: 0.5,
      borderRadius: tokens.radiusChip,
    },
    composerForm: {
      flexShrink: 0,
      px: chromePaddingX,
      py: isHorizontalDock ? 0.75 : 1.5,
      bgcolor: tokens.surfaceChrome,
      backdropFilter: tokens.blurChrome,
      WebkitBackdropFilter: tokens.blurChrome,
      borderTop: `1px solid ${tokens.borderSubtle}`,
      boxShadow: isHorizontalDock ? 'none' : tokens.elevationDock,
    },
  };
};
